import React from "react";
import ReactDOM from "react-dom/client";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { RouterProvider } from "react-router-dom";
import { routes } from "./routes";
import { socket } from "./socket";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { MantineProvider } from "@mantine/core";
import { Settings } from "luxon";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { DeviceTags } from "./DeviceTagContext.tsx";
import { getPresentationParamsForOrigin } from "./getPresentationParamsForOrigin.ts";
import { socketEventMessageSchema } from "./schemas/socketEvents.ts";

Settings.defaultLocale = "de";

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const root = ReactDOM.createRoot(rootElement);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      refetchOnMount: true,
      refetchOnWindowFocus: "always",
      refetchOnReconnect: "always",
      refetchInterval: false,
      refetchIntervalInBackground: false,
    },
  },
});

socket.on("event", async (message: unknown) => {
  const result = socketEventMessageSchema.safeParse(message);
  if (!result.success) {
    console.warn("Invalid event message received:", result.error.message);
    return;
  }

  const { eventType, event } = result.data;
  console.log(eventType, event.id);

  if (eventType.startsWith("game.")) {
    await queryClient.invalidateQueries({
      queryKey: ["game", { gameId: event.id }],
    });
    await queryClient.invalidateQueries({
      queryKey: ["games"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["currentGame"],
    });

    if (eventType == "game.finished") {
      await queryClient.invalidateQueries({
        queryKey: ["leaderboard"],
      });
    }

    if (
      eventType == "game.goalScored" ||
      eventType == "game.goalRevoked" ||
      eventType == "game.goalCorrected" ||
      eventType == "game.goalScorerAssigned"
    ) {
      await queryClient.invalidateQueries({
        queryKey: ["topscorer"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["competitionStats"],
      });
    }

    if (eventType == "game.finished") {
      await queryClient.invalidateQueries({
        queryKey: ["competitionStats"],
      });
    }

    return;
  }

  if (eventType.startsWith("team.")) {
    await queryClient.invalidateQueries({
      queryKey: ["team", { teamId: event.id }],
    });
    await queryClient.invalidateQueries({
      queryKey: ["teams"],
    });
    return;
  }

  if (eventType.startsWith("player.")) {
    await queryClient.invalidateQueries({
      queryKey: ["player", { playerId: event.id }],
    });
    await queryClient.invalidateQueries({
      queryKey: ["players"],
    });
    return;
  }

  if (eventType.startsWith("competition.")) {
    await queryClient.invalidateQueries({
      queryKey: ["competition", { competitionId: event.id }],
    });
    await queryClient.invalidateQueries({
      queryKey: ["competitions"],
    });
    await queryClient.invalidateQueries({
      queryKey: ["leaderboard"],
    });
    return;
  }
});

root.render(
  <React.StrictMode>
    <MantineProvider
      theme={{
        fontFamily: "Chivo",
        fontFamilyMonospace: "Chivo Mono",
        colors: {
          fwcBlue: getPresentationParamsForOrigin().colors.brand,
        },
        primaryColor: "fwcBlue",
      }}
    >
      <QueryClientProvider client={queryClient}>
        <DeviceTags>
          <RouterProvider router={routes} />
          <ReactQueryDevtools
            initialIsOpen={false}
            buttonPosition={"bottom-right"}
          />
        </DeviceTags>
      </QueryClientProvider>
    </MantineProvider>
  </React.StrictMode>,
);
